import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';

import { createDirect, sendMessage } from '../api/client';
import type { UpcomingBirthday } from '../api/types';
import { birthdayGreetingTemplates } from '../i18n/birthdayGreetings';
import { useLocale } from '../i18n/LocaleContext';
import { humanizeError } from '../util/humanizeError';
import { encodeTextPayload } from '../util/chatPayload';
import { hapticError, hapticSelection } from '../util/haptics';
import { organizerStyles as s } from './organizerModalStyles';
import { MESSAGE_TEXT_MAX_CHARS } from '../util/inputLimits';

type Props = {
  /** Кого поздравляем; `null` — окно закрыто. */
  target: UpcomingBirthday | null;
  onClose: () => void;
  /**
   * Поздравление ушло: снаружи по этому событию гасится карточка напоминания.
   *
   * <p>Текст подтверждения передаётся отсюда, а не берётся снаружи: окно закрывается в этот же
   * миг, и показать «Поздравление отправлено» в нём уже негде — увидеть его человек должен там,
   * куда он вернётся.
   */
  onSent: (userId: string, confirmation: string) => void;
};

/**
 * Поздравление с днём рождения (заказ владельца 2026-08-29).
 *
 * <p>Шаблон встаёт в поле ввода, а не уходит сам. Готовое поздравление, отправленное нажатием
 * одной кнопки, — это открытка от приложения, а не от человека, и адресат разницу увидит.
 * Поэтому здесь два шага: выбрал заготовку — поправил под себя — отправил.
 *
 * <p>Сами тексты — в `i18n/birthdayGreetings`: они склоняются по роду именинника и подставляют
 * возраст, и плоскими ключами в `messages.ts` это превратилось бы в `template3Female`.
 */
export function BirthdayGreetingModal({ target, onClose, onSent }: Props): React.ReactElement {
  const { t, locale } = useLocale();

  const [text, setText] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templates = useMemo(
    () =>
      target
        ? birthdayGreetingTemplates(locale, {
            name: target.displayName,
            age: target.turningAge ?? null,
            gender: target.gender ?? null,
          })
        : [],
    [locale, target],
  );

  // Для кого и на каком языке поле заполнено сейчас; смена любого — повод заполнить заново.
  const [seededFor, setSeededFor] = useState<{ target: Props['target']; locale: typeof locale }>({
    target: null,
    locale,
  });
  if (seededFor.target !== target || seededFor.locale !== locale) {
    setSeededFor({ target, locale });
    if (target) {
      // Первый шаблон подставлен сразу: пустое поле рядом со списком заготовок читается как
      // «выберите и ничего не произойдёт», а так человек видит результат выбора с первого взгляда.
      const first = templates[0];
      setText(first?.text ?? '');
      setPickedId(first?.id ?? null);
      setError(null);
    }
  }

  function pick(id: string, value: string) {
    hapticSelection();
    setPickedId(id);
    setText(value);
  }

  async function send() {
    if (!target) return;
    const body = text.trim();
    if (body.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      // Переписки с человеком может ещё не быть — она заводится здесь же, тем же путём, что
      // и обычное «написать»: createDirect идемпотентен и заодно проверяет право писать.
      const chat = await createDirect(target.userId);
      await sendMessage(chat.id, encodeTextPayload(body));
      onSent(target.userId, t('birthdaySent'));
      onClose();
    } catch (e) {
      hapticError();
      setError(humanizeError(e, t('birthdaySendFailed')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal visible={target != null} transparent animationType="fade" onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={s.root}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        enabled={Platform.OS !== 'web'}>
        <Pressable style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }} onPress={onClose} />
        <View style={s.card}>
          <View style={s.headerRow}>
            <Text style={s.title}>{t('birthdayPickTemplate')}</Text>
            <Pressable onPress={onClose} accessibilityRole="button" accessibilityLabel={t('close')}>
              <Text style={s.ghostBtnText}>{t('close')}</Text>
            </Pressable>
          </View>

          <ScrollView style={s.list} keyboardShouldPersistTaps="handled">
            <Text style={s.hint}>{t('birthdayTemplateHint')}</Text>
            <View style={{ height: 8 }} />
            {templates.map((tpl) => {
              const active = pickedId === tpl.id;
              return (
                <Pressable
                  key={tpl.id}
                  style={[s.listItem, active ? { borderColor: 'rgba(96, 165, 250, 0.9)' } : null]}
                  onPress={() => pick(tpl.id, tpl.text)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  accessibilityLabel={tpl.text}>
                  <Text style={s.listItemMeta}>{tpl.text}</Text>
                </Pressable>
              );
            })}

            <View style={{ height: 4 }} />
            <TextInput
              value={text}
              onChangeText={setText}
              maxLength={MESSAGE_TEXT_MAX_CHARS}
              style={[s.input, s.multiline]}
              multiline
              placeholderTextColor="#94a3b8"
              {...(Platform.OS === 'android' ? { textAlignVertical: 'top' as const } : {})}
              accessibilityLabel={t('birthdayPickTemplate')}
            />
            {error ? <Text style={s.error}>{error}</Text> : null}
            <View style={{ height: 10 }} />
            <Pressable
              style={[s.primaryBtn, busy || text.trim().length === 0 ? s.primaryBtnDisabled : null]}
              disabled={busy || text.trim().length === 0}
              onPress={() => void send()}
              accessibilityRole="button"
              accessibilityLabel={t('birthdaySend')}>
              {busy ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={s.primaryBtnText}>{t('birthdaySend')}</Text>
              )}
            </Pressable>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}
